import { useEffect, useRef, useState } from 'react';
import { Button, Card, Input } from '@atoms/index';
import { ConfirmModal } from '@organisms/ConfirmModal';
import {
  complianceService,
  type PlatformLegalDocumentItem,
} from '@services/complianceService';

export function PlatformLegalDocumentsSection() {
  const [documents, setDocuments] = useState<PlatformLegalDocumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [title, setTitle] = useState('Mesafeli Satış Sözleşmesi & KVKK');
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const data = await complianceService.listPlatformLegalDocuments();
      setDocuments(data);
    } catch {
      setError('Yasal dokümanlar yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const activeDocument = documents.find((doc) => doc.isActive);

  const handleFileSelect = (file: File) => {
    if (file.type !== 'application/pdf') {
      setError('Yalnızca PDF dosyaları kabul edilir');
      return;
    }
    setPendingFile(file);
    setShowConfirm(true);
  };

  const handlePublish = async () => {
    if (!pendingFile) return;
    setIsUploading(true);
    setError(null);
    setSuccess(null);
    try {
      await complianceService.publishPlatformLegalDocument(title, pendingFile);
      setSuccess('Yeni versiyon yayınlandı. Tüm restoranlar yeniden onay vermek zorunda.');
      setPendingFile(null);
      await loadDocuments();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Doküman yüklenemedi';
      setError(message);
    } finally {
      setIsUploading(false);
      setShowConfirm(false);
    }
  };

  const handlePreview = async (documentId: string) => {
    try {
      const url = await complianceService.getPlatformLegalDocumentDownloadUrl(documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Önizleme açılamadı');
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-1">Yasal Dokümanlar</h3>
      <p className="text-sm text-gray-500 mb-6">
        Mesafeli Satış Sözleşmesi & KVKK dokümanını yönetin. Yeni versiyon yüklediğinizde tüm
        restoranlar yeniden onay vermek zorunda kalır.
      </p>

      {error && (
        <div className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3 mb-4">{error}</div>
      )}
      {success && (
        <div className="rounded-xl bg-green-50 text-green-700 text-sm px-4 py-3 mb-4">{success}</div>
      )}

      <div className="rounded-2xl border border-gray-200 p-4 mb-6 bg-gray-50/50">
        <p className="text-sm font-medium text-gray-700 mb-2">Aktif Versiyon</p>
        {isLoading ? (
          <p className="text-sm text-gray-500">Yükleniyor...</p>
        ) : activeDocument ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-gray-900">
                v{activeDocument.version} — {activeDocument.title}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {activeDocument.acceptanceCount} restoran onayladı ·{' '}
                {new Date(activeDocument.publishedAt).toLocaleString('tr-TR')}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePreview(activeDocument.id)}
            >
              Önizle
            </Button>
          </div>
        ) : (
          <p className="text-sm text-amber-700">Henüz yüklenmiş bir doküman yok.</p>
        )}
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Doküman Başlığı</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            Yeni PDF Versiyonu Yükle
          </Button>
        </div>
      </div>

      {documents.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-3">Versiyon Geçmişi</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Versiyon</th>
                  <th className="py-2 pr-4">Başlık</th>
                  <th className="py-2 pr-4">Onay</th>
                  <th className="py-2 pr-4">Tarih</th>
                  <th className="py-2">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4">
                      v{doc.version}
                      {doc.isActive && (
                        <span className="ml-2 text-xs text-green-600 font-medium">Aktif</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">{doc.title}</td>
                    <td className="py-3 pr-4">{doc.acceptanceCount}</td>
                    <td className="py-3 pr-4">
                      {new Date(doc.publishedAt).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="py-3">
                      <Button size="sm" variant="ghost" onClick={() => handlePreview(doc.id)}>
                        Görüntüle
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        title="Yeni Versiyon Yayınla"
        description="Bu işlem mevcut aktif versiyonu devre dışı bırakır ve tüm restoranların yeniden onay vermesini zorunlu kılar. Devam etmek istiyor musunuz?"
        confirmText="Yayınla"
        variant="warning"
        isConfirmLoading={isUploading}
        onConfirm={handlePublish}
        onClose={() => {
          setShowConfirm(false);
          setPendingFile(null);
        }}
      />
    </Card>
  );
}
